import { EntityLabel, QuotaUsageKey, Subscription, User } from '@esg/esg-global-types';
import { generateUuidV4 } from '../../util/uuid';
import { MetadataError } from '@ep/error-handling';
import { SubscriptionGridRow } from '../../components/configuration/subscriptions/PanelSubscriptions';

/**
 * Returns true when the user is exempt from export quota limits.
 * EP staff (by email domain) and super-admins do not consume monthly_data_exports quota.
 */
export function isExportQuotaExempt(user: User): boolean {
  return user.super_admin || user.email.toLowerCase().endsWith('@energypartners.co.za');
}

export type ExceededQuotaKey = {
  key: QuotaUsageKey;
  label: string;
};

export type SubscriptionData = Omit<Subscription, 'id'>;

// Singular and plural label for model entity.
export const subscription_label: EntityLabel = {
  one: 'Subscription',
  many: 'Subscriptions'
};

export const default_subscription_key_values: Record<QuotaUsageKey, number> = {
  e_metrics: 0,
  s_metrics: 0,
  g_metrics: 0,
  s_g_metrics: 0,
  emission_factors: 0,
  sites: 0,
  regions: 0,
  entities: 0,
  external_companies: 0,
  subdivisions: 0,
  internal_users: 0,
  monthly_data_exports: 0
};

/**
 * Transforms a company's subscription quota and usage data into a structured array of subscription grid rows.
 * @param {Subscription} subscription_context The subscription object retrieved from company context memory.
 * @returns A Promise resolving to an array of SubscriptionGridRow items containing usage and quota per key.
 * @throws {MetadataError} If the subscription context is missing or processing fails unexpectedly.
 */
export const getCompanySubscriptionFromMemory = (
  subscription_context: Subscription
): Array<SubscriptionGridRow> => {
  try {
    if (!subscription_context) {
      throw new Error('Invalid company context.');
    }

    const company_context_subscription: Subscription = subscription_context ?? {
      quota: {},
      usage: {}
    };

    const quota: Partial<Record<QuotaUsageKey, number>> = company_context_subscription.quota;
    const usage: Partial<Record<QuotaUsageKey, number>> = company_context_subscription.usage;

    const all_quota_keys: Array<QuotaUsageKey> = [
      'e_metrics',
      's_g_metrics',
      'entities',
      'external_companies',
      'internal_users',
      'monthly_data_exports'
    ];

    const subscription_rows: Array<SubscriptionGridRow> = all_quota_keys.map(
      (key: QuotaUsageKey): SubscriptionGridRow => ({
        id: key,
        quota: quota[key] != null ? quota[key].toString() : null,
        usage: usage[key]?.toString() ?? '0'
      })
    );

    return subscription_rows;
  } catch (err: unknown) {
    const tracking_id: string = generateUuidV4();
    throw new MetadataError(
      err instanceof Error
        ? err.message
        : 'Error: getCompanySubscriptionFromMemory failed on an unknown error.',
      {},
      tracking_id
    );
  }
};
